import { PlugToken } from '@plug-core/interfaces/common'
import { ReviewData } from '@plug-core/interfaces/staking'
import { Avatar, Button, Heading, ListGroup, ListRow, Surface, Text } from '@ui'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { HiChevronDoubleRight } from 'react-icons/hi2'

export const StakeReview = ({
  data,
  amount,
  fee,
  mode,
  isLoading,
  icpToken,
  nicpToken,
  submit,
  close,
}: {
  data: ReviewData
  amount: string
  fee: number
  mode: 'stake' | 'unstake'
  isLoading: boolean
  icpToken: PlugToken
  nicpToken: PlugToken
  submit: (action: () => Promise<void>) => void
  close: () => void
}) => {
  const fromToken = mode === 'stake' ? icpToken : nicpToken
  const toToken = mode === 'unstake' ? icpToken : nicpToken

  const { t } = useTranslation()

  const tokenCard = (token: PlugToken, value: string) => (
    <Surface className="flex flex-1 flex-col items-center gap-3 self-stretch">
      <Avatar src={token.logo} fallback={token.symbol} alt={token.symbol} size={40} />
      <div className="flex flex-col gap-1">
        <Heading level={3} align="center" tabular truncate className="w-[100px]">
          {value}
        </Heading>
        <Text variant="caption" tone="fg-2" align="center">
          {token.symbol}
        </Text>
      </div>
    </Surface>
  )

  return (
    <div className="m-2 flex flex-col">
      <Heading level={4} align="center" className="mb-4">
        {t(`stake.review.title.${mode}`)}
      </Heading>

      <div className="relative flex items-center justify-center gap-2">
        {tokenCard(fromToken, amount)}

        <span className="absolute flex rounded-full bg-bg p-2 text-accent">
          <HiChevronDoubleRight size={30} />
        </span>

        {tokenCard(toToken, data.toAmount)}
      </div>

      <ListGroup card={false} className="mt-2">
        {Object.entries({
          ...data.attrs,
          [t('stake.review.fee')]: t('stake.review.feeAmount', {
            fee: fee * 100,
          }),
        }).map(([attr, value]) => (
          <ListRow
            key={attr}
            dense
            primary={attr}
            trailing={<span className="tabular">{value}</span>}
          />
        ))}
      </ListGroup>

      <div className="mt-4 flex flex-col gap-4">
        <Button
          fullWidth
          variant="primary"
          loading={isLoading}
          onClick={() => submit(data.action)}
          disabled={isLoading}
        >
          {t(`stake.review.${mode}`)}
        </Button>

        <Button variant="ghost" fullWidth onClick={close} disabled={isLoading}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  )
}
